import React from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
  useRevalidator,
  useRouteError,
} from "react-router";
import {
  clearPageDataCache,
  createPageLoader,
  isPageDataAuthError,
  loadPageData,
} from "../hooks/usePageData.js";
import {
  getSupabaseSession,
  isSupabaseConfigured,
  signOutSupabaseSession,
  subscribeToSupabaseAuthChanges,
} from "../lib/supabaseClient.js";
import {
  buildAccessState,
  canAccessBillingWorkspacePath,
  canAccessAdminPage,
  canAccessLocalToolsPage,
  canAccessBillingPage,
  canAccessConsolidationPage,
  canAccessDataManagerPage,
  canAccessDispatchPage,
  canAccessGeneralWorkspacePage,
  canAccessGeotabPage,
  canAccessImportPage,
  canManageDriverAccounts,
  canManageAutomations,
  canManageSystemDiagnostics,
  canAccessSystemConfigPage,
  isBillingWorkspaceOnly,
  resolveBillingWorkspaceEntryPath,
} from "../lib/accessControl.js";
import {
  clearCachedEntryPath,
  probeDriverPortalAvailability,
  resolveAuthenticatedEntryPath,
  DRIVER_PORTAL_DELIVERY_PREFIX,
  DRIVER_PORTAL_HOME_PATH,
  OPERATOR_HOME_PATH,
} from "../lib/driverPortal.js";
import useSupabaseInactivityTimeout from "../hooks/useSupabaseInactivityTimeout.js";
import useSessionPresenceHeartbeat from "../hooks/useSessionPresenceHeartbeat.js";
import { getPathnameSectionLabel } from "../utils/appShellNavigation.js";
import useAccess from "../hooks/useAccess.jsx";
import AppShell from "./AppShell.jsx";
import AuthenticatedHomePage from "./AuthenticatedHomePage.jsx";
import DashboardHome from "./DashboardHome.jsx";
import DriverDeliveriesPage from "./DriverPortalPage.jsx";
import LoginPage from "./LoginPage.jsx";
import PublicLandingPage from "./PublicLandingPage.jsx";
import ResetPasswordPage from "./ResetPasswordPage.jsx";
import ResultsPage from "./ResultsPage.jsx";
import UpdatesPage from "./UpdatesPage.jsx";
import LoadingRailPreviewPage from "./LoadingRailPreviewPage.jsx";
import AppLoadingBanner from "./AppLoadingBanner.jsx";
import "./AppShell.css";
import {
  redirectSupabaseRecoveryHashIfNeeded,
} from "../utils/passwordResetRedirect.js";
import { enrichResultsUrlWithStoredDispatchContext } from "../utils/dispatchDateUrls.js";
import { buildResultsLatestBootstrapApiUrl } from "../utils/resultsDispatchBootstrap.js";
import { isDispatchResultsOpening, shouldRevalidateDispatchResults } from "../utils/dispatchResultsNavigation.js";
import { cancelGrainDialogs } from "../utils/grainDialogs.js";
import { clearGeotabSession } from "./geotab/geotabPreferences.js";
import { forgetConsolidationDay, resetConsolidationGeotabGantt } from "../utils/consolidationViewStorage.js";
import { forgetGeotabDays } from "../hooks/useGeotabDay.js";

let pageDataCacheSessionUserId;

function synchronizePageDataCacheSession(session) {
  const nextSessionUserId = typeof session?.user?.id === "string" ? session.user.id.trim() : "";
  if (!nextSessionUserId || pageDataCacheSessionUserId === nextSessionUserId) {
    return;
  }

  forgetConsolidationDay();
  forgetGeotabDays();
  clearPageDataCache();
  pageDataCacheSessionUserId = nextSessionUserId;
}

function resetPageDataCacheSession() {
  clearPageDataCache();
  clearGeotabSession();
  resetConsolidationGeotabGantt();
  forgetConsolidationDay();
  forgetGeotabDays();
  pageDataCacheSessionUserId = undefined;
}

function RouteState({
  action = null,
  message,
  title,
  tone = "loading",
}) {
  if (tone === "loading") {
    return <AppLoadingBanner title={title} message={message} action={action} />;
  }

  const shellClassName = tone === "error"
    ? "border-[rgba(197,82,74,0.18)] bg-[linear-gradient(180deg,rgba(255,244,243,0.96),rgba(255,255,255,0.98))]"
    : "border-black/6 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(243,247,249,0.96))]";
  const eyebrowClassName = tone === "error"
    ? "text-[rgba(178,67,57,0.74)]"
    : "text-[rgba(71,85,105,0.72)]";
  const titleClassName = tone === "error"
    ? "text-danger"
    : "text-slate-900";

  return (
    <section className="mx-auto flex w-full max-w-345 px-5 pt-5 pb-6 max-[720px]:px-4">
      <div className={`relative grid min-h-55 w-full place-items-center ${action ? "rounded-[4px]" : "rounded-[28px]"} border px-6 py-8 shadow-[0_20px_48px_rgba(15,23,42,0.06)] ${shellClassName}`}>
        <div className="grid max-w-130 gap-4 text-center">
          <div className="grid gap-2">
            <p className={`text-[0.68rem] font-black uppercase tracking-[0.18em] ${eyebrowClassName}`}>
              {tone === "error" ? "Chargement interrompu" : "Chargement de la page"}
            </p>
            <h2 className={`text-[clamp(1.35rem,2vw,1.75rem)] font-semibold tracking-[-0.03em] ${titleClassName}`}>
              {title}
            </h2>
            <p className="text-sm leading-7 text-slate-600">
              {message}
            </p>
          </div>
          {action}
        </div>
      </div>
    </section>
  );
}

function resolveRouteErrorMessage(error) {
  if (error instanceof Error && typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }

  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error && typeof error === "object" && "statusText" in error && typeof error.statusText === "string" && error.statusText.trim()) {
    return error.statusText;
  }

  return "Le serveur n'a pas renvoyé de données exploitables.";
}

function RouteErrorState() {
  const error = useRouteError();

  return (
    <RouteState
      tone="error"
      title="La page n'a pas pu être chargée"
      message={resolveRouteErrorMessage(error)}
    />
  );
}

function ResultsRouteErrorState() {
  const error = useRouteError();
  const revalidator = useRevalidator();
  const retrying = revalidator.state !== "idle";
  return (
    <RouteState
      tone="error"
      title="Le dispatch n'a pas pu être chargé"
      message={resolveRouteErrorMessage(error)}
      action={(
        <button
          type="button"
          disabled={retrying}
          onClick={() => revalidator.revalidate()}
          className="justify-self-center rounded-[4px] border border-accent bg-accent px-4 py-2 text-sm font-semibold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none"
        >
          {retrying ? "Chargement…" : "Réessayer"}
        </button>
      )}
    />
  );
}

function LegacyDriverPortalRedirect() {
  const location = useLocation();

  return <Navigate replace to={`${DRIVER_PORTAL_HOME_PATH}${location.search || ""}${location.hash || ""}`} />;
}

function LegacyDriverPortalDeliveryRedirect() {
  const { deliveryId = "" } = useParams();
  const location = useLocation();

  return (
    <Navigate
      replace
      to={`${DRIVER_PORTAL_DELIVERY_PREFIX}/${encodeURIComponent(deliveryId)}${location.search || ""}${location.hash || ""}`}
    />
  );
}

function ProtectedRoute({
  accessCheck,
  children = null,
  denied = null,
  redirectTo = OPERATOR_HOME_PATH,
}) {
  const {
    access,
    errorMessage,
    isAuthError,
    isReady,
  } = useAccess();

  if (isAuthError) {
    return <Navigate replace to="/?authNotice=auth-required&fromApp=1" />;
  }

  if (errorMessage) {
    return (
      <RouteState
        tone="error"
        title="Chargement des accès impossible"
        message={errorMessage}
      />
    );
  }

  if (!isReady) {
    return (
      <RouteState
        title="Vérification des accès"
        message="Nous vérifions les permissions de l'écran demandé avant de l'afficher."
      />
    );
  }

  if (typeof accessCheck === "function" && !accessCheck(access)) {
    const restrictedEntryPath = resolveBillingWorkspaceEntryPath(access);
    // Une page qui exporte AccessDenied explique le droit manquant ; un compte limité garde sa page d'entrée.
    if (denied && !restrictedEntryPath) return denied;
    return <Navigate replace to={restrictedEntryPath || redirectTo} />;
  }

  return children;
}

function ProtectedAppShellLayout() {
  const {
    access,
    errorMessage,
    isAuthError,
    isReady,
    session,
  } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirectedToLandingRef = React.useRef(false);
  const dispatcherAccessUserIdRef = React.useRef("");
  const manualSignOutRedirectRef = React.useRef(false);
  const [isSigningOut, setIsSigningOut] = React.useState(false);
  const sessionUserId = typeof session?.user?.id === "string" ? session.user.id.trim() : "";
  const [dispatcherAccessState, setDispatcherAccessState] = React.useState("checking");

  useSessionPresenceHeartbeat({
    enabled: Boolean(sessionUserId && isReady && !isAuthError),
    section: getPathnameSectionLabel(location.pathname),
    userId: sessionUserId,
  });

  React.useEffect(() => {
    synchronizePageDataCacheSession(session);
  }, [sessionUserId]);

  const redirectToPublicLanding = React.useCallback((authNotice = "auth-required") => {
    if (hasRedirectedToLandingRef.current) {
      return;
    }

    hasRedirectedToLandingRef.current = true;
    resetPageDataCacheSession();
    clearCachedEntryPath();
    const searchParams = new URLSearchParams();
    if (typeof authNotice === "string" && authNotice.trim()) {
      searchParams.set("authNotice", authNotice.trim());
    }
    searchParams.set("fromApp", "1");
    navigate(`/${searchParams.toString() ? `?${searchParams.toString()}` : ""}`, { replace: true });
  }, [navigate]);

  useSupabaseInactivityTimeout({
    onLoggedOut: () => {
      redirectToPublicLanding("session-expired");
    },
  });

  const handleManualSignOut = React.useCallback(async () => {
    if (isSigningOut) {
      return;
    }

    manualSignOutRedirectRef.current = true;
    hasRedirectedToLandingRef.current = false;
    setIsSigningOut(true);

    const { error } = await signOutSupabaseSession({ scope: "local" });
    const { session } = await getSupabaseSession();

    if (error && session) {
      manualSignOutRedirectRef.current = false;
      setIsSigningOut(false);
      return;
    }

    if (session) {
      manualSignOutRedirectRef.current = false;
      setIsSigningOut(false);
      return;
    }

    redirectToPublicLanding("signed-out");
  }, [isSigningOut, redirectToPublicLanding]);

  React.useEffect(() => {
    let isCancelled = false;

    if (!sessionUserId || !isReady || isAuthError || errorMessage) {
      if (!sessionUserId) {
        dispatcherAccessUserIdRef.current = "";
      }
      setDispatcherAccessState("checking");
      return () => {
        isCancelled = true;
      };
    }

    const shouldBlockDuringCheck = dispatcherAccessUserIdRef.current !== sessionUserId;
    if (shouldBlockDuringCheck) {
      setDispatcherAccessState("checking");
    }
    void resolveAuthenticatedEntryPath({ accessState: access, fallbackPath: "" }).then((nextPath) => {
      if (isCancelled) {
        return;
      }

      if (nextPath === OPERATOR_HOME_PATH || nextPath.startsWith("/billing") || nextPath.startsWith("/consolidation")) {
        setDispatcherAccessState("ready");
        dispatcherAccessUserIdRef.current = sessionUserId;
        if (isBillingWorkspaceOnly(access) && !canAccessBillingWorkspacePath(access, location.pathname)) {
          navigate(nextPath || OPERATOR_HOME_PATH, { replace: true });
        }
        return;
      }

      if (nextPath === DRIVER_PORTAL_HOME_PATH) {
        navigate(DRIVER_PORTAL_HOME_PATH, { replace: true });
        return;
      }

      setDispatcherAccessState("blocked");
    });

    return () => {
      isCancelled = true;
    };
  }, [access, errorMessage, isAuthError, isReady, location.pathname, navigate, sessionUserId]);

  React.useEffect(() => {
    const unsubscribe = subscribeToSupabaseAuthChanges((session) => {
      if (!session) {
        const authNotice = manualSignOutRedirectRef.current ? "signed-out" : "session-expired";
        manualSignOutRedirectRef.current = false;
        redirectToPublicLanding(authNotice);
        return;
      }

      hasRedirectedToLandingRef.current = false;
      manualSignOutRedirectRef.current = false;
      setIsSigningOut(false);
    });

    return () => {
      unsubscribe();
    };
  }, [redirectToPublicLanding]);

  React.useEffect(() => {
    if (!isAuthError) {
      return;
    }

    manualSignOutRedirectRef.current = false;
    redirectToPublicLanding("auth-required");
  }, [isAuthError, redirectToPublicLanding]);

  let shellContent = <Outlet />;
  if (errorMessage && !isAuthError) {
    shellContent = (
      <RouteState
        tone="error"
        title="Chargement des accès impossible"
        message={errorMessage}
      />
    );
  } else if (!session || !isReady || dispatcherAccessState === "checking") {
    shellContent = (
      <RouteState
        title="Vérification des accès"
        message="Nous orientons cette session vers l'espace opérateur ou chauffeur approprié."
      />
    );
  } else if (dispatcherAccessState === "blocked") {
    shellContent = (
      <RouteState
        tone="error"
        title="Accès opérateur indisponible"
        message="Cette session n'a pas d'accès opérateur actif. Utilisez le portail chauffeur si un camion est associé à votre compte."
      />
    );
  }

  return (
    <AppShell isSigningOut={isSigningOut} onManualSignOut={handleManualSignOut}>
      {shellContent}
    </AppShell>
  );
}

async function requireSupabaseSession() {
  if (!isSupabaseConfigured()) {
    throw redirect("/?authNotice=auth-required");
  }

  const { error, session } = await getSupabaseSession();
  if (error || !session) {
    throw redirect("/?authNotice=auth-required");
  }

  return session;
}

function createProtectedLoader(loader, { accessCheck = null } = {}) {
  return async function protectedLoader(loaderArgs) {
    try {
      const session = await requireSupabaseSession();
      synchronizePageDataCacheSession(session);

      const accessPayload = typeof accessCheck === "function"
        ? await loadPageData("/api/me/access", { force: true, signal: loaderArgs?.request?.signal })
        : null;
      const accessState = accessPayload ? buildAccessState(accessPayload) : null;
      const nextPath = await resolveAuthenticatedEntryPath({ accessState, fallbackPath: "" });
      if (nextPath === DRIVER_PORTAL_HOME_PATH) {
        throw redirect(DRIVER_PORTAL_HOME_PATH);
      }
      if (!nextPath) {
        throw redirect("/?authNotice=auth-required&fromApp=1");
      }
      if (typeof accessCheck === "function" && !accessCheck(accessState)) {
        throw redirect(resolveBillingWorkspaceEntryPath(accessState) || OPERATOR_HOME_PATH);
      }

      return await loader(loaderArgs);
    } catch (error) {
      if (isPageDataAuthError(error)) {
        resetPageDataCacheSession();
        throw redirect("/?authNotice=session-expired&fromApp=1");
      }
      throw error;
    }
  };
}

function DashboardRoute() {
  return (
    <ProtectedRoute accessCheck={canAccessGeneralWorkspacePage}>
      <DashboardHome />
    </ProtectedRoute>
  );
}

function ResultsRoute() {
  const data = useLoaderData();

  return (
    <ProtectedRoute accessCheck={canAccessDispatchPage}>
      <ResultsPage initialResultsData={data} />
    </ProtectedRoute>
  );
}

function ResultsLatestAliasRoute() {
  const location = useLocation();
  const targetUrl = React.useMemo(
    () => enrichResultsUrlWithStoredDispatchContext(`/results${location.search || ""}${location.hash || ""}`),
    [location.hash, location.search],
  );

  return (
    <Navigate
      replace
      to={targetUrl}
    />
  );
}

function createLazyReadModelRoute({
  accessCheck = null,
  importPage,
  propName,
  routeName,
}) {
  return async function lazyRoute() {
    const pageModule = await importPage();
    const PageComponent = pageModule.default;

    function LazyRouteComponent() {
      const data = useLoaderData();

      const page = <PageComponent {...{ [propName]: data }} />;
      return typeof accessCheck === "function"
        ? <ProtectedRoute accessCheck={accessCheck}>{page}</ProtectedRoute>
        : page;
    }

    LazyRouteComponent.displayName = routeName;

    return { Component: LazyRouteComponent };
  };
}

function createProtectedLazyRoute({
  accessCheck,
  importPage,
  routeName,
}) {
  return async function lazyRoute() {
    const pageModule = await importPage();
    const PageComponent = pageModule.default;
    const AccessDenied = pageModule.AccessDenied;

    function ProtectedLazyRouteComponent() {
      return (
        <ProtectedRoute accessCheck={accessCheck} denied={AccessDenied ? <AccessDenied /> : null}>
          <PageComponent />
        </ProtectedRoute>
      );
    }

    ProtectedLazyRouteComponent.displayName = routeName;

    return { Component: ProtectedLazyRouteComponent };
  };
}

async function driverPortalLoader() {
  const session = await requireSupabaseSession();
  synchronizePageDataCacheSession(session);

  try {
    if (await probeDriverPortalAvailability()) {
      return null;
    }
  } catch {
    // resolveAuthenticatedEntryPath décide ensuite de la meilleure route de repli.
  }

  const nextPath = await resolveAuthenticatedEntryPath({ fallbackPath: "" });
  if (nextPath === DRIVER_PORTAL_HOME_PATH) {
    return null;
  }
  if (nextPath === OPERATOR_HOME_PATH) {
    throw redirect(OPERATOR_HOME_PATH);
  }

  throw redirect("/?authNotice=auth-required&fromApp=1");
}

const dashboardHomeLoader = createProtectedLoader(async () => null, { accessCheck: canAccessGeneralWorkspacePage });
const dashboardReadModelLoader = createProtectedLoader(createPageLoader("/api/dashboard", {
  acceptableStatusCodes: [400],
}), { accessCheck: canAccessGeneralWorkspacePage });
const importLoader = createProtectedLoader(async () => null);
// Dispatch déjà affiché ; rien après une erreur ou depuis une autre section.
function readLoadedDispatchResults() {
  return appRouter.state.errors ? null : appRouter.state.loaderData["dispatch-results"];
}
const resultsLoader = createProtectedLoader(async function resultsPageLoader({ request } = {}) {
  const requestUrl = request?.url ? new URL(request.url) : null;
  if (requestUrl) {
    const restoredRequestUrl = enrichResultsUrlWithStoredDispatchContext(requestUrl.toString());
    if (restoredRequestUrl && restoredRequestUrl !== requestUrl.toString()) {
      const resolvedRestoredUrl = new URL(restoredRequestUrl, requestUrl);
      throw redirect(`${resolvedRestoredUrl.pathname}${resolvedRestoredUrl.search}${resolvedRestoredUrl.hash}`);
    }
  }

  const accessPayload = await loadPageData("/api/me/access", {
    force: true,
    signal: request?.signal,
  });
  if (!canAccessDispatchPage(buildAccessState(accessPayload))) {
    throw redirect(OPERATOR_HOME_PATH);
  }

  const resultsApiUrl = buildResultsLatestBootstrapApiUrl(requestUrl?.search || "", {
    // Les revalidations de la journée affichée gardent la réponse complète.
    deferSourceChanges: isDispatchResultsOpening(requestUrl?.search || "", readLoadedDispatchResults()),
  });
  return loadPageData(resultsApiUrl, {
    acceptableStatusCodes: [404],
    force: true,
    signal: request?.signal,
  });
});
const routeHydrateFallbackElement = <AppRouterFallback />;
const appRouterBasename = typeof window !== "undefined" && window.location.pathname.startsWith("/static/react/")
  ? "/static/react"
  : undefined;

if (typeof window !== "undefined") {
  redirectSupabaseRecoveryHashIfNeeded();
}

export function AppRouterFallback() {
  return (
    <RouteState />
  );
}

function RootRouteSurface() {
  const location = useLocation();
  React.useEffect(() => () => cancelGrainDialogs(), [location.key]);
  return <Outlet />;
}

const appRouter = createBrowserRouter([
  {
    path: "/",
    element: <RootRouteSurface />,
    errorElement: <RouteErrorState />,
    children: [
      {
        index: true,
        Component: PublicLandingPage,
      },
      {
        path: "login",
        Component: LoginPage,
      },
      {
        path: "reset-password",
        Component: ResetPasswordPage,
      },
      {
        path: "portail",
        loader: driverPortalLoader,
        errorElement: <RouteErrorState />,
        hydrateFallbackElement: routeHydrateFallbackElement,
        Component: DriverDeliveriesPage,
      },
      {
        path: "portail/livraisons/:deliveryId",
        loader: driverPortalLoader,
        errorElement: <RouteErrorState />,
        hydrateFallbackElement: routeHydrateFallbackElement,
        Component: DriverDeliveriesPage,
      },
      {
        path: "driver",
        Component: LegacyDriverPortalRedirect,
      },
      {
        path: "driver/deliveries/:deliveryId",
        Component: LegacyDriverPortalDeliveryRedirect,
      },
      {
        element: <ProtectedAppShellLayout />,
        errorElement: <RouteErrorState />,
        children: [
          {
            path: "accueil",
            Component: AuthenticatedHomePage,
          },
          {
            path: "dashboard",
            loader: dashboardHomeLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            Component: DashboardRoute,
          },
          {
            path: "results",
            id: "dispatch-results",
            loader: resultsLoader,
            shouldRevalidate: (args) => shouldRevalidateDispatchResults(args, {
              loadedResults: readLoadedDispatchResults(),
              explicitlyRevalidating: appRouter.state.revalidation !== "idle",
            }),
            errorElement: <ResultsRouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            Component: ResultsRoute,
          },
          {
            path: "results/latest",
            Component: ResultsLatestAliasRoute,
          },
          {
            path: "orders",
            loader: dashboardReadModelLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createLazyReadModelRoute({
              accessCheck: canAccessGeneralWorkspacePage,
              importPage: () => import("./OrdersPage.jsx"),
              propName: "initialReadModel",
              routeName: "OrdersRoute",
            }),
          },
          {
            path: "import",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessImportPage,
              importPage: () => import("./ImportFilesPage.jsx"),
              routeName: "ProtectedImportRoute",
            }),
          },
          {
            path: "reports",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessGeneralWorkspacePage,
              importPage: () => import("./ReportsPage.jsx"),
              routeName: "ProtectedReportsRoute",
            }),
          },
          {
            path: "data-manager",
            loader: async (args) => {
              const url = new URL(args.request.url);
              if (url.searchParams.get("tab") === "approvals" || url.searchParams.get("orderMode") === "approvals") {
                const date = url.searchParams.get("date") || url.searchParams.get("dispatch_date");
                return redirect(`/consolidation${date ? `?date=${encodeURIComponent(date)}` : ""}`);
              }
              return importLoader(args);
            },
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessDataManagerPage,
              importPage: () => import("./DataManagerPage.jsx"),
              routeName: "ProtectedDataManagerRoute",
            }),
          },
          {
            path: "driver-accounts",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canManageDriverAccounts,
              importPage: () => import("./EmployeeDriverAccountsPage.jsx"),
              routeName: "ProtectedDriverAccountsRoute",
            }),
          },
          {
            path: "system-config",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessSystemConfigPage,
              importPage: () => import("./SystemConfigPage.jsx"),
              routeName: "ProtectedSystemConfigRoute",
            }),
          },
          {
            path: "captures",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canManageSystemDiagnostics,
              importPage: () => import("./DispatchCapturesHubPage.jsx"),
              routeName: "ProtectedDispatchCapturesHubRoute",
            }),
          },
          {
            path: "system-config/dispatch-alert-captures",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canManageSystemDiagnostics,
              importPage: () => import("./DispatchAlertCapturesPage.jsx"),
              routeName: "ProtectedDispatchAlertCapturesRoute",
            }),
          },
          {
            path: "system-config/reassignment-diagnostics",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canManageSystemDiagnostics,
              importPage: () => import("./ReassignmentDiagnosticsPage.jsx"),
              routeName: "ProtectedReassignmentDiagnosticsRoute",
            }),
          },
          {
            path: "consolidation",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessConsolidationPage,
              importPage: () => import("./ConsolidationPage.jsx"),
              routeName: "ProtectedConsolidationRoute",
            }),
          },
          {
            path: "geotab",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessGeotabPage,
              importPage: () => import("./GeotabPage.jsx"),
              routeName: "ProtectedGeotabRoute",
            }),
          },
          {
            path: "billing",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessBillingPage,
              importPage: () => import("./BillingPage.jsx"),
              routeName: "ProtectedBillingRoute",
            }),
          },

          {
            path: "admin/local-tools",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessLocalToolsPage,
              importPage: () => import("./LocalToolsPage.jsx"),
              routeName: "ProtectedLocalToolsRoute",
            }),
          },
          {
            path: "admin",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canAccessAdminPage,
              importPage: () => import("./AdminPage.jsx"),
              routeName: "ProtectedAdminRoute",
            }),
          },
          {
            path: "admin/automations",
            loader: importLoader,
            errorElement: <RouteErrorState />,
            hydrateFallbackElement: routeHydrateFallbackElement,
            lazy: createProtectedLazyRoute({
              accessCheck: canManageAutomations,
              importPage: () => import("./AutomationsPage.jsx"),
              routeName: "ProtectedAutomationsRoute",
            }),
          },
          {
            path: "updates",
            element: (
              <ProtectedRoute accessCheck={canAccessGeneralWorkspacePage}>
                <UpdatesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "dev/animation-chargement",
            element: (
              <ProtectedRoute accessCheck={canAccessGeneralWorkspacePage}>
                <LoadingRailPreviewPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
], appRouterBasename ? { basename: appRouterBasename } : undefined);

export default appRouter;
